import { Button, FormControl, InputLabel, MenuItem, Select, TextField, FormHelperText, Box, CircularProgress, Autocomplete } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ALPHABET_REGEX } from '../../constants/regex.ts';
import IResponse from '../../interfaces/response.ts';
import { IDepartment } from '../../interfaces/support.ts';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { DELAY_BEFORE_SEARCH } from '../../constants/numbers.ts';
import { UserContext } from '../../contexts/UserContext.tsx';

const NewTicket = () => {
	const [fetchedDepartments, setFetchedDepartments] = useState<IDepartment[]>();
	const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
	const [options, setOptions] = useState<any[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [value, setValue] = useState<any | null>(null);
	const [search] = useDebounce(inputValue, DELAY_BEFORE_SEARCH);
	const { user } = useContext(UserContext);
	const [loading, setLoading] = useState<boolean>(false);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ticketSchema = z.object({
		title: z
			.string()
			.min(3, t('invalid_min_title_message'))
			.regex(ALPHABET_REGEX, { message: t('invalid_title_message') }),
		department: z.number({
			required_error: t('department_required_message'),
		}),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		control,
	} = useForm<z.infer<typeof ticketSchema>>({
		resolver: zodResolver(ticketSchema),
	});
	const handleSelectDepartment = (e: any) => {
		setSelectedDepartmentId(e.target.value);
	};
	const handleSearch = (_: any, newValue: any) => {
		setValue(newValue);
	};

	useEffect(() => {
		user?.is_admin &&
			(async () => {
				setLoading(true);
				axios({
					url: 'admin/users',
					params: {
						search: JSON.stringify([
							{
								field: 'name',
								value: search,
							},
						]),
					},
				}).then(res => {
					if (res.data.success) {
						setOptions(res.data.data);
					}
				});
				setLoading(false);

				setOptions([...options]);
			})();
	}, [search]);

	const handleNewTicket = (data: z.infer<typeof ticketSchema>) => {
		setLoading(true);
		axios<IResponse<number>>({
			url: user?.is_admin ? '/admin/tickets' : '/tickets',
			method: 'POST',
			data: {
				title: data.title,
				department: selectedDepartmentId + '',
				user_id: user?.is_admin ? value.id : '',
			},
		})
			.then(res => {
				if (res.data.success) {
					user?.is_admin ? navigate('/admin/tickets/list') : navigate('/support/tickets-list');
				}
			})
			.finally(() => {
				setLoading(false);
				reset();
			});
	};
	const handleReset = () => {
		setValue(value);
	};
	useEffect(() => {
		axios<IResponse<IDepartment[]>>({
			url: '/options/ticket_department',
		}).then(res => {
			if (res.data.success) {
				setFetchedDepartments(res.data.data);
			}
		});
	}, []);
	return (
		<div className="h-full w-full items-center justify-center text-5xl">
			{loading ? (
				<Box className="flex min-h-96 w-full items-center justify-center">
					<CircularProgress />
				</Box>
			) : (
				<form className="flex w-full flex-col gap-6 rounded bg-background-paper px-8 pb-0.5 pt-6 text-base" onSubmit={handleSubmit(handleNewTicket)}>
					{user?.is_admin && (
						<Autocomplete
							getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
							filterOptions={x => x}
							options={options}
							autoComplete
							includeInputInList
							filterSelectedOptions
							value={value}
							onChange={handleSearch}
							noOptionsText={t('no_user')}
							onInputChange={(_, newInputValue) => {
								setInputValue(newInputValue);
							}}
							renderOption={(props, option) => {
								return (
									<MenuItem {...props} key={option.id} value={value}>
										{option.name ?? option.phone}
									</MenuItem>
								);
							}}
							renderInput={params => <TextField {...params} label={`${t('search')} `} fullWidth></TextField>}
						/>
					)}
					<div className={'flex flex-col gap-4 sm:flex-row'}>
						<TextField className="w-full text-base" required variant="outlined" label={`${t('title')} `} {...register('title')} error={!!errors.title} helperText={errors.title?.message} />
						<FormControl fullWidth size={'small'} error={!!errors.department}>
							<InputLabel required>{t('department')}</InputLabel>
							<Controller
								name="department"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										value={selectedDepartmentId ?? ''}
										label={t('department')}
										onChange={e => {
											field.onChange(e);
											handleSelectDepartment(e);
										}}
									>
										{fetchedDepartments?.map((dep, i) => (
											<MenuItem key={i} value={dep.id}>
												{dep.title}
											</MenuItem>
										))}
									</Select>
								)}
							/>
							<FormHelperText>{errors.department?.message}</FormHelperText>
						</FormControl>
					</div>
					<div className="mb-8 mt-5 flex flex-row-reverse gap-3">
						<Button variant="outlined" className="my-2 w-28 border-grey-400 text-grey-400" onClick={handleReset}>
							{t('cancel')}
						</Button>
						<Button variant="contained" className="my-2 w-60" type="submit">
							{t('send')}
						</Button>
					</div>
				</form>
			)}
		</div>
	);
};

export default NewTicket;
